import React, { useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Textarea } from 'baseui/textarea';
import { Select, Value } from 'baseui/select';
import { KIND } from 'baseui/notification';
import { aiCompanionApi } from './api';
import { useGuardrailCategories } from './guardrailCategories';

// Violation = one row in guardrails_user_data. The classifier looked at a
// window of messages and emitted one document with a single category +
// severity. The reviewer judges that document. The verdict is upserted on
// (userId, botId, groupId), where groupId is the source row's _id.
export interface FlaggedGroup {
	groupId: string;
	category: string;
	severity?: string | null;
	timestamp?: number;
	messageIds: string[];
	// Joined display fields — one entry per messageId, in the same order.
	messages: Array<{ messageId: string; text?: string; role?: string }>;
}

export interface GuardrailActionRow {
	_id?: string;
	actionType?: string;
	category?: string;
	timestamp?: number;
	warningNumber?: number;
	[k: string]: unknown;
}

export type VerdictTab = 'violation' | 'enforcement';

interface Props {
	userId: string;
	botId: string;
	reviewerEmail: string;
	flagged: FlaggedGroup[];
	actions: GuardrailActionRow[];
	activeTab: VerdictTab;
	onActiveTabChange: (tab: VerdictTab) => void;
	violationIndex: number;
	onViolationIndexChange: (idx: number) => void;
	enforcementIndex: number;
	onEnforcementIndexChange: (idx: number) => void;
	onSubmitted: () => void;
}

type CorrectFlagged = 'yes' | 'no' | 'borderline';
type ReviewerSeverity = 'LOW' | 'MED' | 'HIGH' | 'CRIT' | 'NOT_VIOLATION';
type ActionAppropriate = 'appropriate' | 'too_lenient' | 'too_harsh';
type YesNo = 'yes' | 'no';
// Block-justified options per the mockup: yes / no / escalate. "Escalate"
// is only meaningful for SOFT_BLOCKED → recommend HARD_BLOCKED. For
// HARD_BLOCKED there is no higher tier, so the option is hidden there.
type BlockJustified = 'yes' | 'no' | 'escalate';

interface ViolationRowState {
	correctFlagged?: CorrectFlagged;
	severity?: ReviewerSeverity;
	recategorize: string;
	notes: string;
	submitted?: boolean;
}

// Enforcement form is action-type aware. Block-style actions (BLOCKED /
// SOFT_BLOCKED / HARD_BLOCKED) collect a yes/no on block justification.
// Warn / flagged-for-review collect a 3-way on whether the action was
// proportionate; WARNED additionally asks whether the bot recovered
// properly after the warning landed. The state carries both shapes —
// only the fields that apply for the current action type are read at
// submit / validate time.
interface EnforcementRowState {
	actionAppropriate?: ActionAppropriate;
	botResponseAppropriate?: YesNo;
	blockJustified?: BlockJustified;
	notes: string;
	submitted?: boolean;
}

// MongoDB stores GuardrailActionType values in lowercase (see
// cognilens/companion/constants/enums.py — "warned", "soft_blocked", etc.).
// Compare case-insensitively so the form variant fires regardless of how the
// upstream casing drifts.
const BLOCK_ACTION_TYPES = new Set(['blocked', 'soft_blocked', 'hard_blocked']);
const isBlockAction = (t: string): boolean => BLOCK_ACTION_TYPES.has(t.toLowerCase());
const isWarnAction = (t: string): boolean => t.toLowerCase() === 'warned';

const SEVERITY_OPTIONS: ReviewerSeverity[] = ['LOW', 'MED', 'HIGH', 'CRIT', 'NOT_VIOLATION'];

const labelStyle = {
	fontSize: '12px',
	fontWeight: '600' as const,
	color: '#374151',
	display: 'block',
	marginBottom: '6px'
};

const formatTs = (ts?: number): string => {
	if (!ts) return '—';
	return new Date(ts).toLocaleString();
};

type NotificationKind = typeof KIND[keyof typeof KIND];

const notificationBg = (kind: NotificationKind): string => {
	if (kind === KIND.positive) return '#ecfdf5';
	if (kind === KIND.negative) return '#fef2f2';
	if (kind === KIND.warning) return '#fffbeb';
	return '#f3f4f6';
};
const notificationFg = (kind: NotificationKind): string => {
	if (kind === KIND.positive) return '#065f46';
	if (kind === KIND.negative) return '#991b1b';
	if (kind === KIND.warning) return '#92400e';
	return '#374151';
};
const notificationBorder = (kind: NotificationKind): string => {
	if (kind === KIND.positive) return '#a7f3d0';
	if (kind === KIND.negative) return '#fca5a5';
	if (kind === KIND.warning) return '#fcd34d';
	return '#d1d5db';
};

const pillBorderStyle = (active: boolean, dashed: boolean): string => {
	if (active) return '1px solid #4f46e5';
	if (dashed) return '1px dashed #d1d5db';
	return '1px solid #d1d5db';
};

const SegmentedRadio = <T extends string>({
	options,
	value,
	onChange,
	css,
	vertical
}: {
	options: { id: T; label: string; dashed?: boolean }[];
	value?: T;
	onChange: (v: T) => void;
	css: ReturnType<typeof useStyletron>[0];
	// The mockup stacks block / warn options vertically because the labels
	// are long ("Yes — user deserved to be blocked"). Severity / yes-no-borderline
	// stay horizontal where labels are short.
	vertical?: boolean;
}) => (
	<div
		className={css({
			display: 'flex',
			flexDirection: vertical ? 'column' : 'row',
			alignItems: vertical ? 'stretch' : 'flex-start',
			flexWrap: 'wrap',
			gap: '6px'
		})}>
		{options.map((o) => {
			const active = value === o.id;
			const pillBorder = pillBorderStyle(active, Boolean(o.dashed));
			return (
				<button
					type='button'
					key={o.id}
					onClick={() => onChange(o.id)}
					className={css({
						padding: '8px 12px',
						fontSize: '12px',
						fontWeight: '600',
						borderRadius: '6px',
						border: pillBorder,
						backgroundColor: active ? '#eef2ff' : '#fff',
						color: active ? '#4338ca' : '#374151',
						cursor: 'pointer',
						textAlign: vertical ? 'left' : 'center',
						width: vertical ? '100%' : 'auto'
					})}>
					{o.label}
				</button>
			);
		})}
	</div>
);

const VerdictPanel: React.FC<Props> = ({
	userId,
	botId,
	reviewerEmail,
	flagged,
	actions,
	activeTab,
	onActiveTabChange,
	violationIndex,
	onViolationIndexChange,
	enforcementIndex,
	onEnforcementIndexChange,
	onSubmitted
}) => {
	const [css] = useStyletron();
	const categoryOptions = useGuardrailCategories();
	const [violationState, setViolationState] = useState<Record<string, ViolationRowState>>({});
	const [enforcementState, setEnforcementState] = useState<Record<string, EnforcementRowState>>({});
	const [submitting, setSubmitting] = useState(false);
	const [notification, setNotification] = useState<{ message: string; kind: typeof KIND[keyof typeof KIND] } | null>(null);

	// Auto-dismiss banners so they don't sit on top of the form. Negative
	// (error) banners persist a little longer so the reviewer has time to read
	// what went wrong.
	useEffect(() => {
		if (!notification) return undefined;
		const ms = notification.kind === KIND.negative ? 5000 : 2500;
		const id = setTimeout(() => setNotification(null), ms);
		return () => clearTimeout(id);
	}, [notification]);

	const violationTotal = flagged.length;
	const enforcementTotal = actions.length;
	const safeViolationIndex = Math.min(Math.max(violationIndex, 0), Math.max(violationTotal - 1, 0));
	const safeEnforcementIndex = Math.min(Math.max(enforcementIndex, 0), Math.max(enforcementTotal - 1, 0));
	const currentGroup: FlaggedGroup | undefined = violationTotal > 0 ? flagged[safeViolationIndex] : undefined;
	const currentAction: GuardrailActionRow | undefined = enforcementTotal > 0 ? actions[safeEnforcementIndex] : undefined;
	const currentActionId = currentAction ? String(currentAction._id ?? '') : '';

	useEffect(() => {
		setViolationState((prev) => {
			const next: Record<string, ViolationRowState> = { ...prev };
			flagged.forEach((g) => {
				if (!next[g.groupId]) {
					next[g.groupId] = { recategorize: '', notes: '' };
				}
			});
			return next;
		});
		setEnforcementState((prev) => {
			const next: Record<string, EnforcementRowState> = { ...prev };
			actions.forEach((a) => {
				const aid = String(a._id ?? '');
				if (aid && !next[aid]) {
					next[aid] = { notes: '' };
				}
			});
			return next;
		});
	}, [flagged, actions]);

	const updateViolation = (key: string, patch: Partial<ViolationRowState>) => {
		setViolationState((prev) => ({
			...prev,
			[key]: { ...(prev[key] || { recategorize: '', notes: '' }), ...patch }
		}));
	};

	const updateEnforcement = (key: string, patch: Partial<EnforcementRowState>) => {
		setEnforcementState((prev) => ({
			...prev,
			[key]: { ...(prev[key] || { notes: '' }), ...patch }
		}));
	};

	const violationSubmittedCount = useMemo(
		() => flagged.filter((g) => violationState[g.groupId]?.submitted).length,
		[flagged, violationState]
	);
	const enforcementSubmittedCount = useMemo(
		() => actions.filter((a) => enforcementState[String(a._id ?? '')]?.submitted).length,
		[actions, enforcementState]
	);

	// Advancement rule after a successful submit:
	//   1. If the active tab has more rows, advance within that tab.
	//   2. Else if the other tab has any rows, switch to it (index 0).
	//   3. Else, the reviewer is done with this (userId, botId) pair — bubble
	//      up so the parent can return to the queue.
	const advanceAfterSubmit = () => {
		if (activeTab === 'violation') {
			if (safeViolationIndex < violationTotal - 1) {
				onViolationIndexChange(safeViolationIndex + 1);
				setNotification({ message: 'Saved. Moving to the next violation.', kind: KIND.positive });
				return;
			}
			if (enforcementTotal > 0) {
				onActiveTabChange('enforcement');
				onEnforcementIndexChange(0);
				setNotification({ message: 'Violations done. Switching to enforcement reviews.', kind: KIND.positive });
				return;
			}
		} else {
			if (safeEnforcementIndex < enforcementTotal - 1) {
				onEnforcementIndexChange(safeEnforcementIndex + 1);
				setNotification({ message: 'Saved. Moving to the next enforcement action.', kind: KIND.positive });
				return;
			}
			if (violationTotal > 0) {
				onActiveTabChange('violation');
				onViolationIndexChange(0);
				setNotification({ message: 'Enforcement done. Switching to violations.', kind: KIND.positive });
				return;
			}
		}
		setNotification({ message: 'All reviews submitted. Returning to queue…', kind: KIND.positive });
		setTimeout(onSubmitted, 600);
	};

	const validateCurrent = (): { ok: boolean; reason?: string } => {
		if (!reviewerEmail) {
			return { ok: false, reason: 'Reviewer email not available — please re-login.' };
		}
		if (activeTab === 'violation') {
			if (!currentGroup) return { ok: false, reason: 'No violation selected.' };
			const r = violationState[currentGroup.groupId];
			if (!r?.correctFlagged) return { ok: false, reason: 'Pick "Correctly flagged?".' };
			if (!r?.severity) return { ok: false, reason: 'Pick a severity.' };
			return { ok: true };
		}
		if (!currentAction || !currentActionId) return { ok: false, reason: 'No enforcement action selected.' };
		const r = enforcementState[currentActionId];
		const actionType = String(currentAction.actionType ?? '');
		if (isBlockAction(actionType)) {
			if (!r?.blockJustified) return { ok: false, reason: 'Tell us if this block was justified.' };
		} else {
			if (!r?.actionAppropriate) return { ok: false, reason: 'Tell us if the action was appropriate.' };
			if (isWarnAction(actionType) && !r?.botResponseAppropriate) {
				return { ok: false, reason: "Tell us if the bot's response after the warning was appropriate." };
			}
		}
		return { ok: true };
	};

	const submitCurrent = async (): Promise<boolean> => {
		if (activeTab === 'violation') {
			if (!currentGroup) return false;
			const r = violationState[currentGroup.groupId];
			const result = await aiCompanionApi.submitViolationVerdict({
				userId,
				botId,
				reviewerEmail,
				groupId: currentGroup.groupId,
				messageIds: currentGroup.messageIds,
				systemCategory: currentGroup.category,
				systemSeverity: currentGroup.severity ?? null,
				reviewerCorrectFlagged: r.correctFlagged as CorrectFlagged,
				reviewerSeverity: r.severity as ReviewerSeverity,
				reviewerCategory: r.recategorize.trim() || null,
				reviewerNotes: r.notes.trim() || null
			});
			if (!(result.success && result.data)) {
				setNotification({ message: result.error || 'Failed to submit violation verdict', kind: KIND.negative });
				return false;
			}
			updateViolation(currentGroup.groupId, { submitted: true });
			return true;
		}
		if (!currentAction || !currentActionId) return false;
		const r = enforcementState[currentActionId];
		const actionType = String(currentAction.actionType ?? '');
		const block = isBlockAction(actionType);
		const warn = isWarnAction(actionType);
		const result = await aiCompanionApi.submitEnforcementVerdict({
			userId,
			botId,
			actionId: currentActionId,
			actionType: (currentAction.actionType as string | undefined) ?? null,
			actionCategory: (currentAction.category as string | undefined) ?? null,
			actionTimestamp: typeof currentAction.timestamp === 'number' ? currentAction.timestamp : null,
			reviewerEmail,
			reviewerActionAppropriate: block ? null : (r.actionAppropriate as ActionAppropriate),
			reviewerBotResponseAppropriate: warn ? (r.botResponseAppropriate as YesNo) : null,
			reviewerBlockJustified: block ? (r.blockJustified as BlockJustified) : null,
			reviewerNotes: r.notes.trim() || null
		});
		if (!(result.success && result.data)) {
			setNotification({ message: result.error || 'Failed to submit enforcement verdict', kind: KIND.negative });
			return false;
		}
		updateEnforcement(currentActionId, { submitted: true });
		return true;
	};

	const handleSubmit = async () => {
		const ready = validateCurrent();
		if (!ready.ok) {
			setNotification({ message: ready.reason || 'Form incomplete', kind: KIND.warning });
			return;
		}
		setNotification(null);
		setSubmitting(true);
		try {
			const ok = await submitCurrent();
			if (ok) advanceAfterSubmit();
		} catch (e) {
			setNotification({ message: 'Network error while submitting', kind: KIND.negative });
		}
		setSubmitting(false);
	};

	const renderViolationBody = (g: FlaggedGroup) => {
		const r = violationState[g.groupId] || { recategorize: '', notes: '' };
		return (
			<div key={`v-${g.groupId}`}>
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						marginBottom: '8px',
						gap: '8px'
					})}>
					<div className={css({ fontSize: '12px', fontWeight: '600', color: '#dc2626' })}>{g.category}</div>
					<div className={css({ fontSize: '11px', color: '#6b7280' })}>sys: {g.severity || 'null'}</div>
				</div>
				<div className={css({ fontSize: '11px', color: '#6b7280', marginBottom: '12px' })}>
					{g.messageIds.length} message{g.messageIds.length === 1 ? '' : 's'} in the classifier window ·{' '}
					{formatTs(g.timestamp)}
				</div>
				{r.submitted && (
					<div
						className={css({
							fontSize: '11px',
							color: '#047857',
							backgroundColor: '#ecfdf5',
							border: '1px solid #a7f3d0',
							borderRadius: '6px',
							padding: '4px 8px',
							marginBottom: '10px'
						})}>
						Already submitted — re-submitting overwrites the prior verdict.
					</div>
				)}

				<div className={css({ marginBottom: '10px' })}>
					<label className={css(labelStyle)}>Correctly flagged?</label>
					<SegmentedRadio<CorrectFlagged>
						options={[
							{ id: 'yes', label: 'Yes' },
							{ id: 'no', label: 'No' },
							{ id: 'borderline', label: 'Borderline' }
						]}
						value={r.correctFlagged}
						onChange={(v) => updateViolation(g.groupId, { correctFlagged: v })}
						css={css}
					/>
				</div>

				<div className={css({ marginBottom: '10px' })}>
					<label className={css(labelStyle)}>Correct severity?</label>
					<SegmentedRadio<ReviewerSeverity>
						options={SEVERITY_OPTIONS.map((s) => ({
							id: s,
							label: s === 'NOT_VIOLATION' ? 'Not a violation' : s,
							dashed: s === 'NOT_VIOLATION'
						}))}
						value={r.severity}
						onChange={(v) => updateViolation(g.groupId, { severity: v })}
						css={css}
					/>
				</div>

				<div className={css({ marginBottom: '10px' })}>
					<label className={css(labelStyle)}>Recategorise — optional</label>
					<Select
						options={categoryOptions}
						labelKey='label'
						valueKey='id'
						clearable
						searchable
						placeholder='Pick a category'
						value={
							r.recategorize
								? ([
										categoryOptions.find((o) => o.id === r.recategorize) || {
											id: r.recategorize,
											label: r.recategorize
										}
								  ] as Value)
								: []
						}
						onChange={({ value }) => updateViolation(g.groupId, { recategorize: (value[0]?.id as string) || '' })}
					/>
				</div>

				<div>
					<label className={css(labelStyle)}>Notes — optional</label>
					<Textarea
						value={r.notes}
						onChange={(ev) => updateViolation(g.groupId, { notes: ev.currentTarget.value })}
						rows={2}
					/>
				</div>
			</div>
		);
	};

	const renderEnforcementBody = (a: GuardrailActionRow, actionId: string) => {
		const r = enforcementState[actionId] || { notes: '' };
		const actionType = String(a.actionType ?? '');
		const block = isBlockAction(actionType);
		const warn = isWarnAction(actionType);
		return (
			<div key={`e-${actionId}`}>
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						marginBottom: '8px',
						gap: '8px'
					})}>
					<div className={css({ fontSize: '12px', fontWeight: '600', color: '#b45309' })}>
						{actionType.replace(/_/g, ' ') || 'ACTION'}
					</div>
					<div className={css({ fontSize: '11px', color: '#6b7280' })}>
						{a.warningNumber ? `Warning #${a.warningNumber}` : 'Policy action'}
					</div>
				</div>
				<div className={css({ fontSize: '11px', color: '#6b7280', marginBottom: '12px' })}>
					{a.category ? `${a.category} · ` : ''}
					{formatTs(typeof a.timestamp === 'number' ? a.timestamp : undefined)}
				</div>
				{r.submitted && (
					<div
						className={css({
							fontSize: '11px',
							color: '#047857',
							backgroundColor: '#ecfdf5',
							border: '1px solid #a7f3d0',
							borderRadius: '6px',
							padding: '4px 8px',
							marginBottom: '10px'
						})}>
						Already submitted — re-submitting overwrites the prior verdict.
					</div>
				)}

				{block ? (
					<div className={css({ marginBottom: '12px' })}>
						<label className={css(labelStyle)}>Was this block justified?</label>
						<SegmentedRadio<BlockJustified>
							options={[
								{ id: 'yes', label: 'Yes — user deserved to be blocked' },
								{ id: 'no', label: 'No — should have stayed at warning' },
								// "Escalate" only applies to SOFT_BLOCKED (recommend
								// HARD_BLOCKED). HARD_BLOCKED has no higher tier, so hide it
								// to avoid a meaningless choice.
								...(actionType.toLowerCase() === 'soft_blocked'
									? [{ id: 'escalate' as const, label: 'Escalate — should be hard block' }]
									: [])
							]}
							value={r.blockJustified}
							onChange={(v) => updateEnforcement(actionId, { blockJustified: v })}
							css={css}
							vertical
						/>
					</div>
				) : (
					<>
						<div className={css({ marginBottom: '12px' })}>
							<label className={css(labelStyle)}>Was this action appropriate?</label>
							<SegmentedRadio<ActionAppropriate>
								options={[
									{ id: 'appropriate', label: 'Appropriate' },
									{ id: 'too_lenient', label: 'Too lenient — should have been stronger or sooner' },
									{ id: 'too_harsh', label: 'Too harsh' }
								]}
								value={r.actionAppropriate}
								onChange={(v) => updateEnforcement(actionId, { actionAppropriate: v })}
								css={css}
								vertical
							/>
						</div>
						{warn && (
							<div className={css({ marginBottom: '12px' })}>
								<label className={css(labelStyle)}>Was the bot&apos;s response after the warning appropriate?</label>
								<SegmentedRadio<YesNo>
									options={[
										{ id: 'yes', label: 'Yes — redirected properly' },
										{ id: 'no', label: 'No — kept engaging with the topic' }
									]}
									value={r.botResponseAppropriate}
									onChange={(v) => updateEnforcement(actionId, { botResponseAppropriate: v })}
									css={css}
									vertical
								/>
							</div>
						)}
					</>
				)}

				<div>
					<label className={css(labelStyle)}>Notes — optional</label>
					<Textarea
						value={r.notes}
						onChange={(ev) => updateEnforcement(actionId, { notes: ev.currentTarget.value })}
						rows={2}
					/>
				</div>
			</div>
		);
	};

	const kickerKind = activeTab === 'enforcement' ? 'Enforcement' : 'Violation';
	const kickerColor = activeTab === 'enforcement' ? '#b45309' : '#dc2626';
	const enforcementHeaderTitle = (): string => {
		if (!currentAction) return 'Was this action appropriate?';
		const t = String(currentAction.actionType ?? '');
		if (isBlockAction(t)) return 'Was this block justified?';
		return 'Was this action appropriate?';
	};
	const activeTotal = activeTab === 'violation' ? violationTotal : enforcementTotal;
	const activeIndex = activeTab === 'violation' ? safeViolationIndex : safeEnforcementIndex;
	const activeSubmitted = activeTab === 'violation' ? violationSubmittedCount : enforcementSubmittedCount;
	const onActiveIndexChange = activeTab === 'violation' ? onViolationIndexChange : onEnforcementIndexChange;

	const tabButtonColor = (active: boolean, disabled: boolean): string => {
		if (disabled) return '#9ca3af';
		return active ? '#4338ca' : '#374151';
	};

	const renderTabButton = (tab: VerdictTab, label: string, submitted: number, total: number) => {
		const active = tab === activeTab;
		const disabled = total === 0;
		return (
			<button
				type='button'
				onClick={() => {
					if (!disabled) onActiveTabChange(tab);
				}}
				disabled={disabled}
				className={css({
					flex: '1 1 50%',
					padding: '10px 12px',
					fontSize: '13px',
					fontWeight: '600',
					border: 'none',
					borderBottom: active ? '2px solid #4f46e5' : '2px solid transparent',
					backgroundColor: active ? '#eef2ff' : '#fff',
					color: tabButtonColor(active, disabled),
					cursor: disabled ? 'not-allowed' : 'pointer'
				})}>
				{label}
				<span className={css({ marginLeft: '8px', fontSize: '11px', color: disabled ? '#9ca3af' : '#6b7280' })}>
					{submitted}/{total}
				</span>
			</button>
		);
	};

	return (
		<div
			className={css({
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				backgroundColor: '#fff',
				border: '1px solid #e5e7eb',
				borderRadius: '12px',
				overflow: 'hidden'
			})}>
			<div
				className={css({
					display: 'flex',
					borderBottom: '1px solid #e5e7eb',
					backgroundColor: '#f9fafb'
				})}>
				{renderTabButton('violation', 'Violations', violationSubmittedCount, violationTotal)}
				{renderTabButton('enforcement', 'Enforcement', enforcementSubmittedCount, enforcementTotal)}
			</div>

			<div
				className={css({
					padding: '14px 18px',
					borderBottom: '1px solid #e5e7eb',
					backgroundColor: '#fff',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center'
				})}>
				<div>
					<div
						className={css({
							fontSize: '11px',
							fontWeight: '600',
							color: kickerColor,
							textTransform: 'uppercase',
							letterSpacing: '0.4px'
						})}>
						Verdict — {kickerKind}
					</div>
					<h4 className={css({ fontSize: '15px', fontWeight: '600', color: '#111', margin: '2px 0 0 0' })}>
						{activeTab === 'enforcement' ? enforcementHeaderTitle() : 'Was this correctly flagged?'}
					</h4>
				</div>
				<span className={css({ fontSize: '11px', color: '#6b7280' })}>
					{activeTotal === 0
						? `No ${activeTab === 'violation' ? 'violations' : 'enforcement actions'}`
						: `${activeIndex + 1} of ${activeTotal} · ${activeSubmitted} submitted`}
				</span>
			</div>

			{activeTotal > 0 && (
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						padding: '8px 18px',
						borderBottom: '1px solid #f3f4f6',
						backgroundColor: '#fff',
						gap: '8px'
					})}>
					<Button
						size='compact'
						kind='secondary'
						disabled={activeIndex <= 0}
						onClick={() => onActiveIndexChange(activeIndex - 1)}>
						← Prev
					</Button>
					<Button
						size='compact'
						kind='secondary'
						disabled={activeIndex >= activeTotal - 1}
						onClick={() => onActiveIndexChange(activeIndex + 1)}>
						Next →
					</Button>
				</div>
			)}

			<div className={css({ flex: '1 1 auto', overflowY: 'auto', padding: '16px 18px' })}>
				{activeTab === 'violation' && violationTotal === 0 && (
					<div className={css({ fontSize: '13px', color: '#9ca3af', padding: '20px', textAlign: 'center' })}>
						No violations to review for this conversation.
					</div>
				)}
				{activeTab === 'enforcement' && enforcementTotal === 0 && (
					<div className={css({ fontSize: '13px', color: '#9ca3af', padding: '20px', textAlign: 'center' })}>
						No enforcement actions to review for this conversation.
					</div>
				)}
				{activeTab === 'violation' && currentGroup && renderViolationBody(currentGroup)}
				{activeTab === 'enforcement' &&
					currentAction &&
					currentActionId &&
					renderEnforcementBody(currentAction, currentActionId)}
			</div>

			{notification && (
				<div
					className={css({
						margin: '0 16px 8px',
						padding: '6px 10px',
						fontSize: '12px',
						lineHeight: '1.35',
						borderRadius: '6px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: '8px',
						backgroundColor: notificationBg(notification.kind),
						color: notificationFg(notification.kind),
						border: `1px solid ${notificationBorder(notification.kind)}`
					})}>
					<span>{notification.message}</span>
					<button
						type='button'
						onClick={() => setNotification(null)}
						aria-label='Dismiss'
						className={css({
							background: 'transparent',
							border: 'none',
							color: 'inherit',
							cursor: 'pointer',
							fontSize: '14px',
							lineHeight: 1,
							padding: '0 2px'
						})}>
						×
					</button>
				</div>
			)}

			<div
				className={css({
					padding: '12px 18px',
					borderTop: '1px solid #e5e7eb',
					backgroundColor: '#f9fafb',
					display: 'flex',
					justifyContent: 'flex-end'
				})}>
				<Button onClick={handleSubmit} isLoading={submitting} disabled={activeTotal === 0}>
					Submit & next
				</Button>
			</div>
		</div>
	);
};

export default VerdictPanel;
